import { useEffect, useState } from 'react';
import ModalWrapper from '~/components/ModalWrapper';
import ModalHeader from '~/components/ModalWrapper/ModalHeader';
import Text from '~/components/Text';
import Button from '~/components/Button';
import Gap from '~/components/Gap';
import EmailPreview from '~/components/EmailPreview';
import { checkInvoiceSendApi } from '~/api';
import { useSelector } from '~/store';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import * as Styles from './styles';
import AnimatingDots from '~/components/AnimatingDots';
import TickIcon from '~/icons/TickIcon';

interface IFirstInvoiceModalProps {
  isModalOpen: boolean;
  onCloseModal(): void;
  invoiceUuid: string;
  onOpenSendModal(): void;
}

interface IInvoiceData {
  send_from_email: string;
  send_from_name: string;
  recipient_email?: string;
  recipient_name?: string;
  subject: string;
  content: string;
  invoice_uuid: string;
  pdf_url: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  total_amount: string;
  currency: string;
  business_name: string;
  is_attachment_enabled: boolean;
}

const FirstInvoiceModal = ({
  isModalOpen,
  onCloseModal,
  invoiceUuid,
  onOpenSendModal,
}: IFirstInvoiceModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState<IInvoiceData | null>(null);
  const businessUuid = useSelector(selectBusinessUuid);
  const isDesktop = useIsDesktop();

  const fetchInvoiceData = async () => {
    setIsLoading(true);

    const promise = checkInvoiceSendApi(businessUuid, invoiceUuid);
    const response = await apiResponseHandler<IInvoiceData>(promise);

    if (response.type === ApiResponseEnum.Success) {
      setInvoiceData(response.data);
    }
    setIsLoading(false);
  };

  const handleDownloadPdf = () => {
    if (invoiceData?.pdf_url) {
      window.open(invoiceData.pdf_url, '_blank');
    }
  };

  const handleSendViaEmail = () => {
    onCloseModal();
    onOpenSendModal();
  };

  useEffect(() => {
    if (isModalOpen && invoiceUuid) {
      fetchInvoiceData();
    }
  }, [isModalOpen, invoiceUuid]);

  return (
    <ModalWrapper
      isVisible={isModalOpen}
      modalWidth={isDesktop ? '600px' : 'calc(100vw - 24px)'}
      handleClose={onCloseModal}
    >
      <Styles.Wrapper className="full-width">
        <Styles.StickyHeader>
          <ModalHeader handleClose={onCloseModal} headerJsx="" />
        </Styles.StickyHeader>

        <Styles.ScrollableContent>
          {isLoading || !invoiceData ? (
            <Styles.LoadingContainer className="center">
              <AnimatingDots />
            </Styles.LoadingContainer>
          ) : (
            <>
              <Styles.HeaderSection>
                <Text className="bold center" size="sm">
                  ⚡ Invoice created. Send it instantly
                </Text>
                <Gap t={8} />
                <Text size="xs" className="center" variant="neutral">
                  Send professional invoices directly to your clients in one click.
                </Text>
              </Styles.HeaderSection>

              <Gap t={24} />

              <Styles.PreviewSection>
                <Text size="xs" className="semi-bold" variant="neutral">
                  THIS IS WHAT YOUR CLIENT WILL SEE
                </Text>
                <Gap t={16} />

                <EmailPreview
                  fromEmail={invoiceData.send_from_email}
                  recipientName={invoiceData.recipient_name || 'Customer'}
                  senderName={invoiceData.send_from_name}
                  businessName={invoiceData.business_name}
                  content={invoiceData.content}
                  invoiceNumber={invoiceData.invoice_number}
                  invoiceDate={invoiceData.invoice_date}
                  dueDate={invoiceData.due_date}
                  totalAmount={invoiceData.total_amount}
                  currency={invoiceData.currency}
                  invoiceUrl={`${process.env.LANDING_PAGE_URL}/shared/${invoiceData.invoice_uuid}/`}
                />
              </Styles.PreviewSection>

              <Gap t={24} />

              <Styles.FeaturesSection>
                <Styles.FeatureItem>
                  <TickIcon />
                  <Text size="xs">Professional email template</Text>
                </Styles.FeatureItem>
                <Styles.FeatureItem>
                  <TickIcon />
                  <Text size="xs">Auto-filled client details</Text>
                </Styles.FeatureItem>
                <Styles.FeatureItem>
                  <TickIcon />
                  <Text size="xs">Invoice PDF attached automatically</Text>
                </Styles.FeatureItem>
              </Styles.FeaturesSection>
            </>
          )}
        </Styles.ScrollableContent>

        {!isLoading && invoiceData && (
          <Styles.StickyFooter>
            <Styles.ButtonContainer>
              <Button
                variant="primary"
                onClick={handleSendViaEmail}
                size="md"
                className="full-width"
              >
                Send via email
              </Button>
              <Button
                variant="secondary"
                onClick={handleDownloadPdf}
                size="md"
                className="full-width"
              >
                Download PDF
              </Button>
            </Styles.ButtonContainer>
          </Styles.StickyFooter>
        )}
      </Styles.Wrapper>
    </ModalWrapper>
  );
};

export default FirstInvoiceModal;
